import React, { forwardRef } from 'react';
import { cx, focusRing } from './cx';

export interface IconButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** What the button does — required, because the icon alone tells a screen reader nothing. */
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  tone?: 'neutral' | 'danger' | 'inverse';
  size?: 'sm' | 'md';
}

const TONES = {
  neutral: 'text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100',
  danger: 'text-gray-500 hover:bg-red-50 hover:text-red-600 dark:text-gray-400 dark:hover:bg-red-500/15 dark:hover:text-red-400',
  // For use on a dark or coloured surface.
  inverse: 'text-white/80 hover:bg-white/10 hover:text-white',
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon: Icon, tone = 'neutral', size = 'md', type = 'button', className, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cx(
        'inline-flex flex-none items-center justify-center rounded-lg transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        focusRing,
        TONES[tone],
        size === 'sm' ? 'h-7 w-7' : 'h-9 w-9',
        className,
      )}
      {...rest}
    >
      <Icon className={size === 'sm' ? 'h-4 w-4' : 'h-5 w-5'} aria-hidden="true" />
    </button>
  );
});
